<template>
    <div class="careai__themed-location-tree">
        <div>
            <!-- Search Section -->
            <b-input-group class="careai__themed-tree-search">
                <b-form-input type="text" style="font-size: 10px; height: 40px;" v-model="search" debounce="1500" @click="firstSearch = true;"></b-form-input>
                <b-input-group-append>
                    <span style="opacity: .5;">🔍</span>
                </b-input-group-append>
            </b-input-group>

            <!-- Toolbar Section -->
            <div class="careai__themed-tree-toolbar">
                <a @click="expandAll = true">
                    <font-awesome-icon class="job-details-icon-expand" :style="{ width: 'auto', height: '15px' }" icon="expand" />
                    <span>Expand All</span>
                </a>

                <a @click="collapseAll = true">
                    <font-awesome-icon class="job-details-icon-collapse" :style="{ width: 'auto', height: '15px' }" icon="compress" />
                    <span>Collapse All</span>
                </a>
            </div>

            <!-- Main View Section -->
            <div class="careai__themed-tree-viewport">
               <div v-if="treeDataComputed.length && facility.show" class="careai__themed-tree-viewport-item" v-for="(facility, i) in treeDataComputed" :key="`facility-${i}`">
                    <div class="careai__themed-tree-viewport-item-content"> 
                        <div @click="() => {
                            facility.expanded = !facility.expanded;
                            setFieldData(treeData)
                        }" :class="{
                            'careai__themed-tree-viewport-item--expanded': facility.expanded,
                            'careai__themed-tree-viewport-item--collapsed': !facility.expanded,
                            'careai__themed-tree-viewport-item--caret': true
                        }">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 192 512"><path d="M0 384.7V127.3c0-17.8 21.5-26.7 34.1-14.1l128.7 128.7c7.8 7.8 7.8 20.5 0 28.3L34.1 398.8C21.5 411.4 0 402.5 0 384.7z"/></svg>
                        </div>

                        <b-form-checkbox
                            v-model="facility.selected"
                            @change="(checked) => {
                                facility.selected = checked;
                                if (Array.isArray(facility.children)) {
                                    facility.children.forEach((child) => {
                                        child.selected = checked;
                                        if (Array.isArray(child.children)) {
                                            child.children.forEach((inner) => {
                                                inner.selected = checked;
                                            });
                                        }
                                    });
                                }
                                setFieldData(treeData)
                            }"
                            :value="true"
                            :unchecked-value="false"
                            >
                        </b-form-checkbox>

                        <div>{{ facility.name }}</div>
                    </div>

                    <div v-if="facility.children.length && facility.expanded && area.show" class="careai__themed-tree-viewport-item" v-for="(area, i) in facility.children" :key="`area-${i}`">
                        <div class="careai__themed-tree-viewport-item-content"> 
                            <div @click="() => {
                                area.expanded = !area.expanded;
                                setFieldData(treeData)
                            }" :class="{
                                'careai__themed-tree-viewport-item--expanded': area.expanded,
                                'careai__themed-tree-viewport-item--collapsed': !area.expanded,
                                'careai__themed-tree-viewport-item--caret': true
                            }">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 192 512"><path d="M0 384.7V127.3c0-17.8 21.5-26.7 34.1-14.1l128.7 128.7c7.8 7.8 7.8 20.5 0 28.3L34.1 398.8C21.5 411.4 0 402.5 0 384.7z"/></svg>
                            </div>

                            <b-form-checkbox
                                v-model="area.selected"
                                @change="(checked) => {
                                    area.selected = checked;
                                    if (!checked) {
                                        facility.selected = false;
                                    }
                                    if (Array.isArray(area.children)) {
                                        area.children.forEach((child) => {
                                            child.selected = checked;
                                        });
                                    }
                                    setFieldData(treeData)
                                }"
                                :value="true"
                                :unchecked-value="false"
                                >
                            </b-form-checkbox>

                            <div>{{ area.name }}</div>
                        </div>

                        <div v-if="area.children.length && area.expanded && location.show" class="careai__themed-tree-viewport-item" v-for="(location, i) in area.children" :key="`location-${i}`">
                            <div class="careai__themed-tree-viewport-item-content"> 
                                <div :class="{
                                    'careai__themed-tree-viewport-item--expanded': location.expanded,
                                    'careai__themed-tree-viewport-item--collapsed': !location.expanded,
                                    'careai__themed-tree-viewport-item--caret': true
                                }" style="opacity: 0; pointer-events: none;">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 192 512"><path d="M0 384.7V127.3c0-17.8 21.5-26.7 34.1-14.1l128.7 128.7c7.8 7.8 7.8 20.5 0 28.3L34.1 398.8C21.5 411.4 0 402.5 0 384.7z"/></svg>
                                </div>

                                <b-form-checkbox
                                    v-model="location.selected"
                                    @change="(checked) => {
                                        location.selected = checked;
                                        if (!checked) {
                                            facility.selected = false;
                                            area.selected = false;
                                        }
                                        setFieldData(treeData)
                                    }"
                                    :value="true"
                                    :unchecked-value="false"
                                    >
                                </b-form-checkbox>

                                <div>{{ location.name }}</div>
                            </div>
                        </div>
                    </div>
               </div>
               <div v-if="!treeDataComputed.length" class="careai__themed-tree-viewport-item" style="padding: 10px;">
                    No locations available in this tenant.
               </div>
            </div>

            <!-- Add Delete -->
            <div v-if="selected && selected.length" class="careai__themed-tree-toolbar careai__themed-tree-bottom-toolbar careai__themed-tree-bottom-toolbar-delete">
                <span v-for="(item, i) in selected" :key="`selected-item-${i}-${item.name}`" class="careai__themed-tree-delete">
                    {{ item.name }}
                    <a @click.stop="() => {
                        removeSelected(item);
                    }">
                        <span>X</span>
                    </a>
                </span>
            </div>

            <!-- Bottom Toolbar Section -->
            <div class="careai__themed-tree-toolbar careai__themed-tree-bottom-toolbar">
                <a @click.stop="() => {
                    treeDataComputed.forEach((fac) => {
                        fac.selected = false;
                        fac.active = false;
                        if (Array.isArray(fac.children)) {
                            fac.children.forEach((unit) => {
                                unit.selected = false;
                                unit.active = false;
                                if (Array.isArray(unit.children)) {
                                    unit.children.forEach((area) => {
                                        area.selected = false;
                                        area.active = false;
                                    })
                                }
                            })
                        }
                    })
                    setFieldData(treeData)
                }">
                    <span>Clear All</span>
                </a>

                <a @click.stop="() => {
                    setFieldData(treeData)
                    startSelectionDebounce();
                }">
                    <span>Ok</span>
                </a>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "ThemedLocationTree",
    components: {},
    props: {
        treeData: {
            type: Array,
            default: () => ([
                // MODEL EXPECTED
                // {
                //     id: '',
                //     name: '',
                //     expanded: true,
                //     selected: false,
                //     children: [
                //         {
                //             id: '', 
                //             name: '',
                //             expanded: true,
                //             selected: false,
                //             children: [
                //                 {
                //                     id: '', 
                //                     name: '',
                //                     expanded: true,
                //                     selected: false,
                //                 }
                //             ]
                //         }
                //     ]
                // }
            ]),
        },
        fieldRef: {
            type: Object,
            default: () => (null),
        },
        activeLocationSearch: {
            type: String,
            default: '', 
        }
    },
    data() {
        return {
            console: console,
            search: '',
            baseDelay: 2000,
            firstSearch: false,
            expandAll: false,
            collapseAll: false,
            debounceTimeout: null,
        };
    },
    watch: {
        searchCompute() {

        },
        treeDataComputed() {
            this.collapseAll = false;
            this.expandAll = false;
            this.firstSearch = false;
        }
    },
    computed: {
        selected() {
            const areas = [];
            this.treeDataComputed.forEach((fac) => {
                if (Array.isArray(fac.children)) {
                    fac.children.forEach((unit) => {
                        if (Array.isArray(unit.children)) {
                            unit.children.forEach((area) => {
                                if (area.selected) {
                                    area.fac = fac.id;
                                    area.uni = unit.id;
                                    areas.push(area);
                                }
                            })
                        }
                    })
                }
            })
            return areas;
        },
        searchCompute() {
            this.$emit('searchChange', this.search);
            return this.search;
        },
        treeDataComputed() {
            this.treeData.forEach((facility) => {
                facility.show = false;
                if (
                    !this.search || 
                    (facility.name && facility.name.toLowerCase().includes(this.search.toLocaleLowerCase()))) {
                    facility.show = true;
                } 
                if (this.firstSearch || this.expandAll) facility.expanded = true;
                if (this.collapseAll) facility.expanded = false;
                if (Array.isArray(facility.children)) {
                    facility.children.forEach((area) => {
                        area.show = false;
                        if (
                            !this.search || 
                            (
                                (facility.name && facility.name.toLowerCase().includes(this.search.toLocaleLowerCase())) ||
                                (area.name && area.name.toLowerCase().includes(this.search.toLocaleLowerCase()))
                            )
                        ) {
                            facility.show = true;
                            area.show = true;
                        } 
                        if (this.firstSearch || this.expandAll) area.expanded = true;
                        if (this.collapseAll) area.expanded = false;
                        area.selected = facility.selected || area.selected;
                        if (Array.isArray(area.children)) {
                            area.children.forEach((location) => {
                                location.show = false;
                                if (
                                    !this.search || 
                                    (
                                        (facility.name && facility.name.toLowerCase().includes(this.search.toLocaleLowerCase())) ||
                                        (area.name && area.name.toLowerCase().includes(this.search.toLocaleLowerCase())) || 
                                        (location.name && location.name.toLowerCase().includes(this.search.toLocaleLowerCase()))
                                    )
                                ) {
                                    facility.show = true;
                                    area.show = true;
                                    location.show = true;
                                } 
                                if (this.firstSearch || this.expandAll) location.expanded = true;
                                if (this.collapseAll) location.expanded = false;
                                location.selected = facility.selected || area.selected || location.selected;
                            })
                            if ((area.children.every((child) => child.selected) && area.children.length) || facility.selected) {
                                area.selected = true;
                            } else area.selected = false;
                        }
                    });
                    if (facility.children.every((child) => child.selected) && facility.children.length) {
                        facility.selected = true;
                    } else facility.selected = false;
                }
            });
            return this.treeData;
        }
    },
    mounted() {
        this.search = this.activeLocationSearch;
        this.treeData.forEach((fac) => {
            fac.expanded = false;
            if (Array.isArray(fac.children) && fac.children.length) {
                fac.children.forEach((unit) => {
                    if (Array.isArray(unit.children) && unit.children.length) {
                        unit.children.forEach((area) => {
                            if (area.active) {
                                area.selected = true;
                            } else area.selected = false;
                        });
                        if (unit.children.every((child) => child.selected)) {
                            unit.selected = true;
                        } else unit.selected = false;
                    }
                });
                if (fac.children.every((child) => child.selected)) {
                    fac.selected = true;
                } else fac.selected = false;
            }
        });
    },
    methods: {
        removeSelected(item) {
            try {
                const data = [...this.treeData];
                const fac = data.find((child) => child.id === item.fac);
                fac.selected = false;
                const uni = fac.children.find((child) => child.id === item.uni);
                uni.selected = false;
                const area = uni.children.find((child) => child.id === item.id)
                area.selected = false;
                if (this.fieldRef) this.fieldRef.treeData = data;
            } catch (e) { /* */ }
        },
        setFieldData(data) {
            if (this.fieldRef) this.fieldRef.treeData = data;
        },
        startSelectionDebounce() {
            try {
                clearTimeout(this.debounceTimeout);
            } catch { /* PASS */ }
            this.debounceTimeout = null;
            this.debounceTimeout = setTimeout(() => {
                this.emitSelection();
            }, 500);
        },
        emitSelection: function() {
            const areas = [];
            this.treeDataComputed.forEach((fac) => {
                fac.expanded = false;
                if (Array.isArray(fac.children)) {
                    fac.children.forEach((unit) => {
                        unit.expanded = false;
                        if (Array.isArray(unit.children)) {
                            unit.children.forEach((area) => {
                                area.expanded = false;
                                if (area.selected) {
                                    area.active = true;
                                    areas.push(area.id);
                                } else area.active = false;
                            })
                        }
                    })
                }
            })
            this.$emit("areasChange", areas);
        }
    },
};
</script>

<style lang="scss">
    .careai__themed-location-tree {
        width: 100%;
        color: #6a6a6a;
        font-weight: 400;

        .form-control {
            border: 0px solid transparent;
        }

        div {
            .careai__themed-tree-search {
                padding: 10px;
                .input-group-append {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    padding: 5px;
                    border: 1px solid #ebebeb;
                    border-left-color: transparent;
                    border-radius: 5px;
                    border-top-left-radius: 0px;
                    border-bottom-left-radius: 0px;
                }

                input {
                    border: 1px solid #ebebeb;
                    border-right-width: 0px;
                }

                .input-group-append {
                    border-left-width: 0px;
                }

                .input-group-append, input {
                    transition: all 150ms;
                }

                &:hover {
                    .input-group-append, input {
                        border-color: #1dd8f6 !important;
                    }
                }
            }

            .careai__themed-tree-toolbar {
                padding: 10px;
                display: flex;
                justify-content: space-between;
                align-items: center;
                border-top: 1px solid #ebebeb;
                border-bottom: 1px solid #ebebeb;

                a {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    gap: 5px;
                    transition: color 250ms;

                    &:hover {
                        color: #1dd8f6;

                        > svg {
                            color: #1dd8f6;
                        }
                    }
                }
            }

            .careai__themed-tree-bottom-toolbar {
                border-bottom: 0px;
                justify-content: flex-end;
                gap: 10px;

                a {
                    background-color: #fff;
                    color: #8E8E8E;
                    border: 1px solid #E8E8E8;
                    border-radius: 5px;
                    width: 80px;
                    padding: 10px 15px;

                    &:hover {
                        background-color: #1dd8f6;
                        color: #fff;
                    }
                }
            }

            .careai__themed-tree-bottom-toolbar-delete {
                display: flex;
                gap: 6px;
                align-items: center;
                max-height: 108px;
                overflow-y: auto;
                overflow-x: hidden;
                flex-wrap: wrap;
                justify-content: flex-start;
                margin-bottom: 1px;
                
                .careai__themed-tree-delete {
                    display: flex;
                    gap: 4px;
                    align-items: center;
                    max-height: 100px;
                    overflow-y: auto;
                    overflow-x: hidden;
                    flex-wrap: wrap;
                    font-size: 12px !important;
                    margin-right: 3px;

                    a {
                        height: 15px;
                        width: 15px;
                        background-color: #f27778;
                        transition: all .25s;
                        padding: 2px;
                        border-radius: 50%;
                        color: white;

                        > span {
                            font-size: 8px !important;
                        }

                        &:hover {
                            background-color: #d96969;
                        }
                    }
                }
            }

            .careai__themed-tree-viewport {
                max-height: 400px;
                overflow-y: auto;
                overflow-x: hidden;
                padding: 10px 10px 10px 0px;

                > div {
                    display: flex;
                    align-items: center;
                    flex-wrap: wrap;
                }

                .careai__themed-tree-viewport-item {
                    width: 100%;

                    > .careai__themed-tree-viewport-item-content {
                        margin-left: 10px;
                        padding: 10px;
                        display: flex;
                        gap: 10px;
                        border-radius: 5px;
                        flex-wrap: wrap;
                        align-items: center;
                        flex: 1;

                        &:hover {
                            background-color: #eaf7ff;
                            cursor: pointer;
                        }
                        .careai__themed-tree-viewport-item--caret {
                            > svg {
                                height: 15px;
                                width: auto;
                            }

                            &:hover {
                                > svg {
                                    fill: #1dd8f6;
                                }       
                            }
                        }
                        .custom-control-input:checked~.custom-control-label:before {
                            color: #1dd8f6;
                            border-color: #1dd8f6;
                            background-color: #fff;
                        }
                        .custom-checkbox .custom-control-input:checked~.custom-control-label:after {
                            background-image: none;
                            content: '✓';
                            display: flex;
                            justify-content: center;
                            color: #1dd8f6;
                            padding-top: 1px;
                            padding-right: 1px;
                            font-weight: 800;
                            font-size: 10px;
                        }
                        .careai__themed-tree-viewport-item--expanded {
                            position: relative;
                            transform: rotate(90deg);
                        }
                        > div {
                            align-items: center;
                            font-size: 12px !important;
                        }
                    }

                    .careai__themed-tree-viewport-item .careai__themed-tree-viewport-item-content  {
                        margin-left: 20px;    
                    }

                    .careai__themed-tree-viewport-item .careai__themed-tree-viewport-item .careai__themed-tree-viewport-item-content  {
                        margin-left: 30px;    
                    }
                }
            }
        }
    }
</style>
